import React, { useCallback, useEffect, useMemo, useState } from 'react';
import SignalSparkline from './SignalSparkline';
import { SIGNAL_REFRESH_MS } from './signalRefresh';
import { useNavigate } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import {
  listPublicMarketplace,
  getLeaderboard,
  formatSignalMoney,
} from './signalDataFeeds';
import { DerivAffiliateBanner } from './DerivAffiliate';
import './SignalsMarketplacePage.css';

// Public signal marketplace at /signals.
//
// This is the discovery surface — the reason someone who has never built a strategy would land
// on the site at all, and the top of the funnel for VixPremium. It is deliberately open to
// signed-out visitors.
//
// Everything shown is the LIVE portion of a track record. Seed history exists only to make a
// creator's own chart readable on day one and is never advertised here.

const SORT_OPTIONS = [
  { id: 'return', label: 'Return' },
  { id: 'winRate', label: 'Win rate' },
  { id: 'drawdown', label: 'Lowest drawdown' },
  { id: 'trades', label: 'Most signals' },
  { id: 'age', label: 'Longest running' },
];

function SignalCard({ entry, onOpen, onCreator }) {
  const { deployment, stats, badges, daysLive, meetsRankingBar, sparkline, sparklineTimes, listing } = entry;
  // Times ride alongside the values so the curve can be read, not just seen. Older servers
  // send no times; the curve still draws, it just has nothing to label the x axis with.
  const curve = (sparkline || []).map((value, index) => ({
    t: (sparklineTimes || [])[index], v: value,
  }));
  return (
    <article className="market-card">
      <header>
        <div>
          <h3>{deployment.name}</h3>
          <button type="button" className="market-creator" onClick={() => onCreator(deployment.creatorId)}>
            by {deployment.creatorName}
          </button>
        </div>
        <div className="market-head-right">
          {/* Followable = the creator has published it. Free with VixPremium, so the badge
              says what you need rather than what it costs. */}
          {listing && <span className="market-price-pill">VixPremium</span>}
          {meetsRankingBar
            ? <span className="signal-pill ok">Verified</span>
            : <span className="signal-pill">Proving</span>}
        </div>
      </header>

      <div className="market-chips">
        <span className="signal-chip">{deployment.symbol}</span>
        <span className="signal-chip">{deployment.timeframe}</span>
        <span className="signal-chip">{daysLive}d live</span>
      </div>

      {listing?.headline && <p className="market-headline">{listing.headline}</p>}

      <SignalSparkline points={curve} label="Equity" className="market-spark-host" />

      <div className="market-metrics">
        <div>
          <span>Return</span>
          <strong className={stats.returnPct >= 0 ? 'up' : 'down'}>
            {stats.returnPct >= 0 ? '+' : ''}{stats.returnPct.toFixed(1)}%
          </strong>
        </div>
        <div>
          <span>Win rate</span>
          <strong>{stats.trades ? `${stats.winRate.toFixed(0)}%` : '—'}</strong>
        </div>
        <div>
          <span>Signals</span>
          <strong>{stats.trades}</strong>
        </div>
        <div>
          <span>Max DD</span>
          <strong>{stats.maxDrawdownPct.toFixed(1)}%</strong>
        </div>
      </div>

      {badges.length > 0 && (
        <div className="market-badges">
          {badges.slice(0, 3).map((badge) => (
            <span key={badge.id} className="market-badge">{badge.label}</span>
          ))}
        </div>
      )}

      <footer>
        <span className="muted-text">
          {listing
            ? `${listing.subscriberCount || 0} follower${listing.subscriberCount === 1 ? '' : 's'}`
            : `Net ${formatSignalMoney(stats.netProfit)}`}
        </span>
        <button type="button" className="primary-cta compact-button" onClick={() => onOpen(deployment.id)}>
          {listing ? 'Follow signal' : 'View & follow'}
        </button>
      </footer>
    </article>
  );
}

function SignalsMarketplacePage() {
  const navigate = useNavigate();
  const [signals, setSignals] = useState([]);
  const [leaders, setLeaders] = useState([]);
  const [facets, setFacets] = useState({ symbols: [], timeframes: [] });
  const [filters, setFilters] = useState({ symbol: '', timeframe: '', sort: 'return', ranked: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const payload = await listPublicMarketplace(filters);
      setSignals(payload.signals || []);
      setFacets({ symbols: payload.symbols || [], timeframes: payload.timeframes || [] });
      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);
  // The marketplace used to load once and never look again, so a card could sit a day behind the
  // same signal's detail page. Same cadence as every other signal view now.
  useEffect(() => {
    const timer = setInterval(load, SIGNAL_REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  useEffect(() => {
    getLeaderboard({ sort: 'return' })
      .then((payload) => setLeaders(payload.leaderboard || []))
      .catch(() => setLeaders([]));
  }, []);

  const openSignal = (id) => navigate(`/signals/${id}`);
  const openCreator = (id) => navigate(`/signals/creators/${id}`);

  return (
    <HelmetProvider>
      <div className="market-page page-shell">
        <Helmet>
          <title>Live Trading Signals | Vix Algo Trading</title>
          <meta
            name="description"
            content="Browse verified live trading signals from strategies running on real market data. Follow any signal in a free practice account."
          />
        </Helmet>

        <header className="market-hero">
          <div>
            <h1>Live signals</h1>
            <p>
              Strategies running on live market data, executed on our servers and tracked bar by
              bar. Every track record here is produced by the platform — not self-reported — and
              the strategy behind it is locked from the day it went live.
            </p>
            <div className="market-hero-actions">
              <button type="button" className="primary-cta" onClick={() => navigate('/tester')}>
                Build your own
              </button>
              {/* The other way to get a signal here: bring an EA you already own. It goes through
                  the importer — upload, check, backtest — and deploys from there. */}
              <button type="button" className="ghost-button" onClick={() => navigate('/import-ea')}>
                Import an EA
              </button>
              <button type="button" className="ghost-button" onClick={() => navigate('/dashboard?tab=signals')}>
                My signals
              </button>
            </div>
          </div>
        </header>

        {leaders.length > 0 && (
          <section className="market-leaderboard">
            <h2>Leaderboard</h2>
            <p className="muted-text">Top verified signals by return. Only signals past the proving bar appear here.</p>
            <ol>
              {leaders.slice(0, 5).map((entry, index) => (
                <li key={entry.deployment.id}>
                  <button type="button" onClick={() => openSignal(entry.deployment.id)}>
                    <span className="market-rank">{index + 1}</span>
                    <span className="market-lead-name">
                      <strong>{entry.deployment.name}</strong>
                      <small className="muted-text">
                        {entry.deployment.symbol} {entry.deployment.timeframe} · by {entry.deployment.creatorName}
                      </small>
                    </span>
                    <span className={`market-lead-return ${entry.stats.returnPct >= 0 ? 'up' : 'down'}`}>
                      {entry.stats.returnPct >= 0 ? '+' : ''}{entry.stats.returnPct.toFixed(1)}%
                    </span>
                  </button>
                </li>
              ))}
            </ol>
          </section>
        )}

        {/* Placed after the leaderboard: someone who has just seen the track records is the
            person for whom "you need an MT5 account to copy these" is actually useful. */}
        <DerivAffiliateBanner variant="signals" compact />

        <section className="market-controls">
          <label>
            <span>Symbol</span>
            <select
              value={filters.symbol}
              onChange={(event) => setFilters((prev) => ({ ...prev, symbol: event.target.value }))}
            >
              <option value="">All</option>
              {facets.symbols.map((symbol) => <option key={symbol} value={symbol}>{symbol}</option>)}
            </select>
          </label>
          <label>
            <span>Timeframe</span>
            <select
              value={filters.timeframe}
              onChange={(event) => setFilters((prev) => ({ ...prev, timeframe: event.target.value }))}
            >
              <option value="">All</option>
              {facets.timeframes.map((tf) => <option key={tf} value={tf}>{tf}</option>)}
            </select>
          </label>
          <label>
            <span>Sort by</span>
            <select
              value={filters.sort}
              onChange={(event) => setFilters((prev) => ({ ...prev, sort: event.target.value }))}
            >
              {SORT_OPTIONS.map((option) => (
                <option key={option.id} value={option.id}>{option.label}</option>
              ))}
            </select>
          </label>
          <label className="market-toggle">
            <input
              type="checkbox"
              checked={filters.ranked}
              onChange={(event) => setFilters((prev) => ({ ...prev, ranked: event.target.checked }))}
            />
            <span>Verified only</span>
          </label>
        </section>

        {error && <p className="signal-error">{error}</p>}

        {loading ? (
          <p className="muted-text">Loading signals…</p>
        ) : signals.length === 0 ? (
          <div className="market-empty">
            <h3>No public signals yet</h3>
            <p className="muted-text">
              Nobody has shared a signal publicly yet. Build a strategy, deploy it live, and set
              its visibility to public to be the first.
            </p>
            <button type="button" className="primary-cta compact-button" onClick={() => navigate('/tester')}>
              Open the Builder
            </button>
          </div>
        ) : (
          <div className="market-grid">
            {signals.map((entry) => (
              <SignalCard
                key={entry.deployment.id}
                entry={entry}
                onOpen={openSignal}
                onCreator={openCreator}
              />
            ))}
          </div>
        )}

        <p className="market-disclaimer muted-text">
          Signals are informational and are not financial advice. Past performance — verified or
          otherwise — does not guarantee future results. Trading carries risk of loss.
        </p>
      </div>
    </HelmetProvider>
  );
}

export default SignalsMarketplacePage;
